import { useMutation, useQuery } from "@apollo/client";
import useLogIncidentInteraction from "api/mutations/useLogIncidentInteraction";
import { graphql } from "gql/apollo";
import { useCallback, useMemo } from "react";
import useSnackbar from "utils/useSnackbar";

export const ListSalesforceIntegrationsQuery = graphql(/* GraphQL */ `
	query listSalesforceIntegrations {
		listSalesforceIntegrations {
			_id
		}
	}
`);

export const SendMutation = graphql(/* GraphQL */ `
	mutation sendIncidentToSalesforce($params: SendIncidentToSalesforceParams!) {
		sendIncidentToSalesforce(params: $params) {
			error {
				code
				message
			}
			success
		}
	}
`);

export function useSendIncidentToSalesforce({
	incidentId,
}: {
	incidentId: string;
}) {
	const { handleSuccess, handleError } = useSnackbar();
	const { data, loading: isQueryLoading } = useQuery(
		ListSalesforceIntegrationsQuery
	);
	const [sendToSalesforce] = useMutation(SendMutation);
	const { mutate: logIncidentInteraction } = useLogIncidentInteraction();

	const hasSalesforceIntegration = useMemo(
		() => (data?.listSalesforceIntegrations?.length ?? 0) > 0,
		[data?.listSalesforceIntegrations?.length]
	);

	const salesforceIntegrationId = useMemo(
		() => data?.listSalesforceIntegrations?.[0]?._id ?? "",
		[data?.listSalesforceIntegrations]
	);

	const sendIncidentToSalesforce = useCallback(async () => {
		if (!hasSalesforceIntegration) return;

		try {
			const res = await sendToSalesforce({
				variables: {
					params: {
						incident_id: incidentId,
						integration_id: salesforceIntegrationId,
					},
				},
			});

			if (res.data?.sendIncidentToSalesforce.error) {
				handleError(res.data?.sendIncidentToSalesforce.error);
			} else if (res.data?.sendIncidentToSalesforce.success) {
				handleSuccess("Incident sent to Salesforce");
				logIncidentInteraction({
					incident_id: incidentId,
					interaction_type: "salesforce",
					source: "dashboard",
				});
			}
		} catch (e) {
			handleError(e);
		}
	}, [
		hasSalesforceIntegration,
		incidentId,
		salesforceIntegrationId,
		handleError,
		handleSuccess,
		logIncidentInteraction,
		sendToSalesforce,
	]);

	return {
		isQueryLoading,
		hasSalesforceIntegration,
		sendIncidentToSalesforce,
	};
}
